import { useRef, useState } from "react";
import Modal from "@modalSrc/Modal";
import Helpers from "@/Helpers";

type TestOutputDataPreview = {
    realtimePayloads?: unknown[];
    postSectionPayload?: unknown;
};

type TestOutputDataModalProps = {
    isOpen: boolean;
    onClose: () => void;
    payloadPreview?: TestOutputDataPreview;
    maxLineLength?: number;
};

export default function TestOutputDataModal(props: TestOutputDataModalProps) {
    const {
        isOpen,
        onClose,
        payloadPreview,
        maxLineLength = 200,
    } = props;
    const [leftPanelWidth, setLeftPanelWidth] = useState(50);
    const splitContainerRef = useRef<HTMLDivElement | null>(null);

    function onSplitterMouseDown(e: React.MouseEvent<HTMLDivElement>) {
        e.preventDefault();

        const onMouseMove = (moveEvent: MouseEvent) => {
            const container = splitContainerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const offsetX = moveEvent.clientX - rect.left;
            const percentage = (offsetX / rect.width) * 100;
            const clamped = Math.min(75, Math.max(25, percentage));
            setLeftPanelWidth(clamped);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    }

    return (
        <Modal
            isOpen={!!isOpen}
            onClose={onClose}
            title="Preview Outbound Payloads"
            size="lg"
            className="testOutputDataModal"
            zIndex={1300}
            footer={(
                <div className="footerRight">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            )}
        >
            <div
                ref={splitContainerRef}
                style={{ display: "flex", width: "100%", height: "100%", minHeight: 0 }}
            >
                <div
                    style={{
                        flex: `0 0 ${leftPanelWidth}%`,
                        minWidth: 0,
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <strong>/RealTimeResponse</strong>
                    <textarea
                        disabled
                        aria-label="Realtime response payload preview"
                        title="Realtime response payload preview"
                        value={Helpers.formatJsonForEditor(payloadPreview?.realtimePayloads || [], maxLineLength, 2)}
                        style={{
                            marginTop: "6px",
                            width: "100%",
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflow: "auto",
                            background: "#f5f5f5",
                            padding: "10px",
                            boxSizing: "border-box",
                            textAlign: "left",
                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                            fontSize: "13px",
                            lineHeight: "1.4",
                        }}
                    />
                </div>

                <div
                    role="separator"
                    aria-orientation="vertical"
                    onMouseDown={onSplitterMouseDown}
                    style={{
                        width: "8px",
                        cursor: "col-resize",
                        backgroundColor: "#e5e7eb",
                        borderLeft: "1px solid #d1d5db",
                        borderRight: "1px solid #d1d5db",
                        margin: "0 8px",
                    }}
                />

                <div
                    style={{
                        flex: `1 1 ${100 - leftPanelWidth}%`,
                        minWidth: 0,
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <strong>/PostSection</strong>
                    <textarea
                        disabled
                        aria-label="Post section payload preview"
                        title="Post section payload preview"
                        value={Helpers.formatJsonForEditor(payloadPreview?.postSectionPayload || {}, maxLineLength, 2)}
                        style={{
                            marginTop: "6px",
                            width: "100%",
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflow: "auto",
                            background: "#f5f5f5",
                            padding: "10px",
                            boxSizing: "border-box",
                            textAlign: "left",
                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                            fontSize: "13px",
                            lineHeight: "1.4",
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
}


